import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { getHotMedia, getLatestMedia, getLatestNews } from "@/utils/media";

export default function Home() {
  usePageMeta({
    title: "17C.COM - 最新电影电视剧资讯与在线播放平台",
    description:
      "17C.COM提供丰富的电影、电视剧资源和详细影视资讯，覆盖热门影视作品，用户可查询最新动态与影评，轻松找到心仪影视内容。",
    keywords:
      "17C.COM,影视资讯,电影推荐,电视剧更新,在线影视,影视动态,影评",
  });

  const hotMedia = getHotMedia(8);
  const latestMedia = getLatestMedia(6);
  const news = getLatestNews(4);

  return (
    <div className="w-full">
      <HeroCarousel />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {categories.map((cat) => (
            <Link
              key={cat.key}
              to={`/category/${cat.key}`}
              className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-200 hover:bg-primary-50 md:p-5"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={`${cat.icon} text-2xl`} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {cat.name}
                </h3>
                <p className="mt-0.5 line-clamp-2 text-xs text-foreground-500">
                  {cat.description}
                </p>
              </div>
              <i
                className="ri-arrow-right-line shrink-0 text-xl text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500"
                aria-hidden="true"
              />
            </Link>
          ))}
        </div>
      </section>

      {/* 热门影视推荐 */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-10 md:px-6 md:pb-12">
        <SectionHeading
          icon="ri-fire-line"
          title="热门影视推荐"
          subtitle="根据站内热度排序，实时更新最受关注的影视作品"
          moreTo="/category"
          moreLabel="全部分类"
        />
        {hotMedia.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {hotMedia.map((item, index) => (
              <MovieCard key={item.id} item={item} rank={index + 1} />
            ))}
          </div>
        ) : (
          <EmptyState title="暂无内容，敬请期待" />
        )}
      </section>

      {/* 最新资讯 */}
      <section id="news" className="border-y border-background-200 bg-background-100 py-10 md:py-12">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeading
            icon="ri-newspaper-line"
            title="最新影视资讯"
            subtitle="影视动态、影评与行业观察，第一时间掌握"
            moreTo="/news"
            moreLabel="更多资讯"
          />
          {news.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {news.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState title="暂无内容，敬请期待" />
          )}
        </div>
      </section>

      {/* 最新上线 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <SectionHeading
          icon="ri-time-line"
          title="最新上线"
          subtitle="近期更新的影视作品，追新不落伍"
          moreTo="/category"
          moreLabel="浏览全部"
        />
        {latestMedia.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {latestMedia.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState title="暂无内容，敬请期待" />
        )}
      </section>

      {/* 网站简介 */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-6">
        <div className="grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-3 md:p-8">
          <div className="md:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              关于 17C.COM
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              17C.COM 致力于为影视爱好者打造一个内容丰富的综合影视门户。我们提供最新的电影、电视剧、综艺资讯与影评，帮助用户快速了解影视动态；同时提供清晰的分类浏览与影视作品详情展示，让你更高效地发现值得一看的作品。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              站内影视作品资料与资讯均为演示内容，播放页仅提供播放器界面演示。我们持续优化内容呈现与使用体验，期待与你一起发现更多好作品。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-information-line text-base" aria-hidden="true" />
                了解我们
              </Link>
              <Link
                to="/category"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-play-list-line text-base" aria-hidden="true" />
                影视分类
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
            {[
              { value: "22+", label: "影视作品", icon: "ri-film-line" },
              { value: "8", label: "最新资讯", icon: "ri-newspaper-line" },
              { value: "3", label: "内容频道", icon: "ri-folders-line" },
              { value: "24h", label: "持续更新", icon: "ri-refresh-line" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-3 rounded-lg bg-secondary-100 p-4"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className={`${stat.icon} text-lg`} aria-hidden="true" />
                </span>
                <div>
                  <p className="font-heading text-lg font-black text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}